import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Layout } from '../../components/layout/Layout';
import api from '../../api';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { 
  ArrowLeft, Edit, Trash2, Phone, Building2, MapPin, Tag, Hash,
  Wifi, Network, Calendar, FileText, User
} from 'lucide-react';
import { usePermissions } from '../../hooks/usePermissions';
import { toast } from 'sonner';

const TelefonoIPDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { canUpdate, canDelete } = usePermissions('micro', 'telefonos_ip');
  
  const [phone, setPhone] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editModal, setEditModal] = useState(false);
  const [formData, setFormData] = useState({});
  const [formOptions, setFormOptions] = useState({ brands: [], statuses: [], sites: [], empresas: [], ubicaciones: [], proveedores: [], usuarios: [] });
  const [saving, setSaving] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);

  const fetchPhone = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/assets/telefonos-ip/${id}`);
      setPhone(response.data);
    } catch (error) {
      toast.error('Error al cargar el teléfono');
      navigate('/micro/ip-phones');
    } finally {
      setLoading(false);
    }
  };

  const fetchFormOptions = async () => {
    try {
      const response = await api.get('/assets/telefonos-ip/form/options');
      setFormOptions(response.data);
    } catch (error) {
      console.error('Error fetching form options:', error);
    }
  };

  useEffect(() => {
    fetchPhone();
    fetchFormOptions();
  }, [id]);

  const openEditModal = () => {
    setFormData({
      empresa_id: phone.empresa_id?.toString() || '',
      site_id: phone.site_id?.toString() || '',
      brand_id: phone.brand_id?.toString() || '',
      asset_tag: phone.asset_tag || '',
      serial_number: phone.serial_number || '',
      modelo: phone.modelo || '',
      ip_address: phone.ip_address || '',
      mac_address: phone.mac_address || '',
      status_id: phone.status_id?.toString() || '',
      location_id: phone.location_id?.toString() || '',
      extension: phone.extension || '',
      assigned_user_id: phone.assigned_user_id?.toString() || '',
      supplier_id: phone.supplier_id?.toString() || '',
      purchase_date: phone.purchase_date || '',
      comments: phone.comments || ''
    });
    setEditModal(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        ...formData,
        empresa_id: formData.empresa_id ? parseInt(formData.empresa_id) : null,
        site_id: formData.site_id ? parseInt(formData.site_id) : null,
        brand_id: formData.brand_id ? parseInt(formData.brand_id) : null,
        status_id: formData.status_id ? parseInt(formData.status_id) : null,
        location_id: formData.location_id ? parseInt(formData.location_id) : null,
        assigned_user_id: formData.assigned_user_id ? parseInt(formData.assigned_user_id) : null,
        supplier_id: formData.supplier_id ? parseInt(formData.supplier_id) : null
      };

      await api.put(`/assets/telefonos-ip/${id}`, payload);
      toast.success('Teléfono actualizado exitosamente');
      setEditModal(false);
      fetchPhone();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/assets/telefonos-ip/${id}`);
      toast.success('Teléfono eliminado exitosamente');
      navigate('/micro/ip-phones');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar');
    }
  };

  const getStatusBadge = (statusName) => {
    if (!statusName) return <span className="badge badge-warning">Sin Estado</span>;
    const lower = statusName.toLowerCase();
    if (lower.includes('activo')) return <span className="badge badge-success">{statusName}</span>;
    if (lower.includes('inactivo') || lower.includes('baja')) return <span className="badge badge-danger">{statusName}</span>;
    return <span className="badge badge-default">{statusName}</span>;
  };

  if (loading) {
    return <Layout><div className="flex items-center justify-center h-64"><p className="text-muted-foreground">Cargando...</p></div></Layout>;
  }

  if (!phone) return null;

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate('/micro/ip-phones')}>
              <ArrowLeft className="h-4 w-4 mr-2" strokeWidth={1.5} />
              Volver
            </Button>
            <div>
              <div className="flex items-center gap-3">
                <Phone className="h-6 w-6 text-primary" strokeWidth={1.5} />
                <h1 className="text-2xl font-bold">{phone.brand_name || 'Teléfono'} {phone.modelo || ''}</h1>
              </div>
              <p className="text-muted-foreground font-mono">{phone.asset_tag}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {canUpdate && (
              <Button variant="outline" onClick={openEditModal}>
                <Edit className="h-4 w-4 mr-2" strokeWidth={1.5} />Editar
              </Button>
            )}
            {canDelete && (
              <Button variant="destructive" onClick={() => setDeleteModal(true)}>
                <Trash2 className="h-4 w-4 mr-2" strokeWidth={1.5} />Eliminar
              </Button>
            )}
          </div>
        </div>

        {/* Summary */}
        <Card className="bg-muted/30">
          <CardContent className="p-4">
            <div className="flex flex-wrap items-center gap-6">
              {getStatusBadge(phone.status_name)}
              {phone.extension && (
                <div className="flex items-center gap-2 text-sm">
                  <Phone className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
                  <span>Ext. {phone.extension}</span>
                </div>
              )}
              {phone.ip_address && (
                <div className="flex items-center gap-2 text-sm">
                  <Wifi className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
                  <span className="font-mono">{phone.ip_address}</span>
                </div>
              )}
              {phone.assigned_user_name && (
                <div className="flex items-center gap-2 text-sm">
                  <User className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
                  <span>{phone.assigned_user_name}</span>
                </div>
              )}
              {phone.site_name && (
                <div className="flex items-center gap-2 text-sm">
                  <Building2 className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
                  <span>{phone.site_name}</span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Detail Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card>
            <CardHeader><CardTitle className="text-base flex items-center gap-2"><Hash className="h-4 w-4" strokeWidth={1.5} />Identificación</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div className="flex justify-between"><span className="text-muted-foreground">Asset Tag</span><span className="font-mono">{phone.asset_tag || '-'}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Serial Number</span><span className="font-mono">{phone.serial_number || '-'}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Marca</span><span>{phone.brand_name || '-'}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Modelo</span><span>{phone.modelo || '-'}</span></div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle className="text-base flex items-center gap-2"><Network className="h-4 w-4" strokeWidth={1.5} />Red y Telefonía</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div className="flex justify-between"><span className="text-muted-foreground">Extensión</span><span className="font-mono">{phone.extension || '-'}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">IP Address</span><span className="font-mono">{phone.ip_address || '-'}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">MAC Address</span><span className="font-mono">{phone.mac_address || '-'}</span></div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle className="text-base flex items-center gap-2"><MapPin className="h-4 w-4" strokeWidth={1.5} />Ubicación y Asignación</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div className="flex justify-between"><span className="text-muted-foreground">Empresa</span><span>{phone.empresa_name || '-'}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Site</span><span>{phone.site_name || '-'}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Ubicación</span><span>{phone.location_name || '-'}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Usuario Asignado</span><span>{phone.assigned_user_name || '-'}</span></div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle className="text-base flex items-center gap-2"><FileText className="h-4 w-4" strokeWidth={1.5} />Compra y Notas</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div className="flex justify-between"><span className="text-muted-foreground">Proveedor</span><span>{phone.supplier_name || '-'}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Fecha de Compra</span><span>{phone.purchase_date || '-'}</span></div>
              {phone.comments && (
                <div className="pt-2 border-t">
                  <p className="text-sm text-muted-foreground mb-1">Comentarios</p>
                  <p className="text-sm">{phone.comments}</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Edit Modal */}
        <Dialog open={editModal} onOpenChange={setEditModal}>
          <DialogContent className="max-w-2xl" aria-describedby="edit-phone-desc">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><Edit className="h-5 w-5" strokeWidth={1.5} />Editar Teléfono</DialogTitle>
            </DialogHeader>
            <p id="edit-phone-desc" className="text-sm text-muted-foreground">Modifique los datos del teléfono IP.</p>
            
            <div className="grid grid-cols-2 gap-4 py-4">
              <div className="space-y-2"><Label>Asset Tag *</Label><Input value={formData.asset_tag} onChange={(e) => setFormData(prev => ({ ...prev, asset_tag: e.target.value }))} /></div>
              <div className="space-y-2"><Label>Serial Number</Label><Input value={formData.serial_number} onChange={(e) => setFormData(prev => ({ ...prev, serial_number: e.target.value }))} /></div>
              <div className="space-y-2"><Label>Marca</Label><Select value={formData.brand_id} onValueChange={(v) => setFormData(prev => ({ ...prev, brand_id: v }))}><SelectTrigger><SelectValue placeholder="Seleccionar..." /></SelectTrigger><SelectContent>{formOptions.brands.map(b => <SelectItem key={b.id} value={b.id.toString()}>{b.nombre}</SelectItem>)}</SelectContent></Select></div>
              <div className="space-y-2"><Label>Modelo</Label><Input value={formData.modelo} onChange={(e) => setFormData(prev => ({ ...prev, modelo: e.target.value }))} /></div>
              <div className="space-y-2"><Label>Extensión</Label><Input value={formData.extension} onChange={(e) => setFormData(prev => ({ ...prev, extension: e.target.value }))} /></div>
              <div className="space-y-2"><Label>IP Address</Label><Input value={formData.ip_address} onChange={(e) => setFormData(prev => ({ ...prev, ip_address: e.target.value }))} /></div>
              <div className="space-y-2"><Label>MAC Address</Label><Input value={formData.mac_address} onChange={(e) => setFormData(prev => ({ ...prev, mac_address: e.target.value }))} /></div>
              <div className="space-y-2"><Label>Usuario Asignado</Label><Select value={formData.assigned_user_id} onValueChange={(v) => setFormData(prev => ({ ...prev, assigned_user_id: v }))}><SelectTrigger><SelectValue placeholder="Seleccionar..." /></SelectTrigger><SelectContent>{formOptions.usuarios.map(u => <SelectItem key={u.id} value={u.id.toString()}>{u.nombre}</SelectItem>)}</SelectContent></Select></div>
              <div className="space-y-2"><Label>Empresa *</Label><Select value={formData.empresa_id} onValueChange={(v) => setFormData(prev => ({ ...prev, empresa_id: v }))}><SelectTrigger><SelectValue placeholder="Seleccionar..." /></SelectTrigger><SelectContent>{formOptions.empresas.map(e => <SelectItem key={e.id} value={e.id.toString()}>{e.nombre}</SelectItem>)}</SelectContent></Select></div>
              <div className="space-y-2"><Label>Site *</Label><Select value={formData.site_id} onValueChange={(v) => setFormData(prev => ({ ...prev, site_id: v }))}><SelectTrigger><SelectValue placeholder="Seleccionar..." /></SelectTrigger><SelectContent>{formOptions.sites.map(s => <SelectItem key={s.id} value={s.id.toString()}>{s.nombre}</SelectItem>)}</SelectContent></Select></div>
              <div className="space-y-2"><Label>Ubicación</Label><Select value={formData.location_id} onValueChange={(v) => setFormData(prev => ({ ...prev, location_id: v }))}><SelectTrigger><SelectValue placeholder="Seleccionar..." /></SelectTrigger><SelectContent>{formOptions.ubicaciones.map(u => <SelectItem key={u.id} value={u.id.toString()}>{u.nombre}</SelectItem>)}</SelectContent></Select></div>
              <div className="space-y-2"><Label>Estado</Label><Select value={formData.status_id} onValueChange={(v) => setFormData(prev => ({ ...prev, status_id: v }))}><SelectTrigger><SelectValue placeholder="Seleccionar..." /></SelectTrigger><SelectContent>{formOptions.statuses.map(s => <SelectItem key={s.id} value={s.id.toString()}>{s.nombre}</SelectItem>)}</SelectContent></Select></div>
              <div className="space-y-2"><Label>Proveedor</Label><Select value={formData.supplier_id} onValueChange={(v) => setFormData(prev => ({ ...prev, supplier_id: v }))}><SelectTrigger><SelectValue placeholder="Seleccionar..." /></SelectTrigger><SelectContent>{formOptions.proveedores.map(p => <SelectItem key={p.id} value={p.id.toString()}>{p.nombre}</SelectItem>)}</SelectContent></Select></div>
              <div className="space-y-2"><Label>Fecha de Compra</Label><Input type="date" value={formData.purchase_date} onChange={(e) => setFormData(prev => ({ ...prev, purchase_date: e.target.value }))} /></div>
              <div className="space-y-2 col-span-2"><Label>Comentarios</Label><Textarea value={formData.comments} onChange={(e) => setFormData(prev => ({ ...prev, comments: e.target.value }))} rows={2} /></div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setEditModal(false)}>Cancelar</Button>
              <Button onClick={handleSave} disabled={saving}>{saving ? 'Guardando...' : 'Guardar'}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Modal */}
        <Dialog open={deleteModal} onOpenChange={setDeleteModal}>
          <DialogContent aria-describedby="del-phone-desc">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-destructive"><Trash2 className="h-5 w-5" strokeWidth={1.5} />Eliminar Teléfono</DialogTitle>
            </DialogHeader>
            <p id="del-phone-desc" className="text-muted-foreground">¿Eliminar <strong>{phone.asset_tag}</strong>?<br /><span className="text-sm">Esta acción no se puede deshacer.</span></p>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteModal(false)}>Cancelar</Button>
              <Button variant="destructive" onClick={handleDelete}>Eliminar</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default TelefonoIPDetail;
